import { useEffect, useSyncExternalStore } from 'react';

import { API_FINANCES_SUMMARY } from './EsConstants';
import { esRead } from './EsFetch';
import { INTL_FALLBACK, esFormatDollars, esFormatNumber } from './EsIntl';
import { esLiteral, useEsStrings } from './EsStrings';

/**
 * What Escapade costs and how many subscribers cover it, as finances/summary
 * answers it, and the words a reader with no account is shown for it.
 *
 * <p>ONE store, read once per page load: the home page and the Subscribe page
 * both show it, and neither changes it.
 */
const EMPTY = { summary: null, loaded: false, error: null };

let gState = EMPTY;
let gInFlight = null;
const gWatchers = new Set();

const setState = ( pNext ) => {
    gState = pNext;
    gWatchers.forEach( ( pNotify ) => pNotify() );
};

const subscribe = ( pNotify ) => {
    gWatchers.add( pNotify );
    return () => gWatchers.delete( pNotify );
};

const snapshot = () => gState;

function esFinancesRefresh() {
    if ( gInFlight ) return gInFlight;

    gInFlight = esRead( API_FINANCES_SUMMARY )
        .then( ( pSummary ) => setState( { summary: pSummary, loaded: true, error: null } ) )
        .catch( ( pError ) => setState( { summary: null, loaded: true, error: pError.message } ) )
        .finally( () => { gInFlight = null; } );

    return gInFlight;
}

/** Tests only: forget what was read. */
export function esFinancesReset() {
    gInFlight = null;
    setState( EMPTY );
}

/**
 * { summary, loaded, error }.
 */
export function useEsFinances() {
    const aState = useSyncExternalStore( subscribe, snapshot, snapshot );

    useEffect( () => {
        if ( !aState.loaded ) esFinancesRefresh();
    }, [ aState.loaded ] );

    return aState;
}

// ---- the words ------------------------------------------------------------

/*
 * The expense words, by the row's own ids, in the reader's locale: each helper takes the screen's t
 * last, esLiteral (English) by default. Public keys, though only the admin screens show them
 * today, because Tim asked for them translated (2026-10-19); the admin screens' own keys stay
 * en-US only. A category reads as its id, as the screens always showed it.
 */
export const CATEGORIES = [ 'hosting', 'software', 'fees', 'domains', 'other' ];
export const RECURRENCES = [ 'none', 'monthly', 'yearly' ];

export function esCategoryText( pCategory, t = esLiteral ) {
    switch ( pCategory ) {
        case 'hosting': return t( 'common-finance-category-hosting', 'hosting' );
        case 'software': return t( 'common-finance-category-software', 'software' );
        case 'fees': return t( 'common-finance-category-fees', 'fees' );
        case 'domains': return t( 'common-finance-category-domains', 'domains' );
        case 'other': return t( 'common-finance-category-other', 'other' );
        default: return pCategory ?? '';
    }
}

export function esRecursText( pRecurs, t = esLiteral ) {
    switch ( pRecurs ) {
        case 'none': return t( 'common-finance-recurs-none', 'One time' );
        case 'monthly': return t( 'common-finance-recurs-monthly', 'Monthly' );
        case 'yearly': return t( 'common-finance-recurs-yearly', 'Yearly' );
        default: return pRecurs ?? '';
    }
}

/** The admin form's and filter's choices: the reader's words over the ids. */
export const esCategoryOptions = ( t = esLiteral ) => CATEGORIES.map( ( pId ) => ( { label: esCategoryText( pId, t ), value: pId } ) );
export const esRecursOptions = ( t = esLiteral ) => RECURRENCES.map( ( pId ) => ( { label: esRecursText( pId, t ), value: pId } ) );

/**
 * "$219.34" from 21934, "$35" from 3500, "$80,832" from 8083200: cents only when there are some.
 * In the reader's locale; the admin screens, whose copy is en-US only, pass INTL_FALLBACK.
 */
export function esMoney( pCents, pTag ) {
    return esFormatDollars( Math.round( pCents ?? 0 ), pTag );
}

/** monthly-costs.md §7: a GM brings five free players, so an active sub is six accounts. Java's EsFinanceService.ACCOUNTS_PER_SUB. */
export const ACCOUNTS_PER_SUB = 6;

/**
 * What a row's scaling adds to its month at pAccounts active accounts (V088): the accounts
 * past scaleFree, at scaleCents per scalePer of them -- pro rata, rounded half up to the
 * cent, or with scaleBlock per started block. The same sum as EsFinanceService.scaledCents.
 */
export function esScaledCents( pRow, pAccounts ) {
    const aCents = pRow?.scaleCents ?? 0;
    if ( !aCents ) return 0;
    const aPer = pRow.scalePer >= 1 ? pRow.scalePer : 1;
    const aOver = Math.max( 0, pAccounts - ( pRow.scaleFree ?? 0 ) );
    if ( pRow.scaleBlock ) return Math.ceil( aOver / aPer ) * aCents;
    return Math.floor( ( aOver * aCents * 2 + aPer ) / ( 2 * aPer ) );
}

/** A monthly row's month at pSubs active subs: its amount and its scaling; null for a row that is not monthly. */
export function esMonthAt( pRow, pSubs ) {
    if ( pRow?.recurs !== 'monthly' ) return null;
    return ( pRow.amountCents ?? 0 ) + esScaledCents( pRow, pSubs * ACCOUNTS_PER_SUB );
}

/** "+15¢ per 10 accounts past 9900", or "" for a fixed row. The numbers stay en-US, as the admin screens' do. */
export function esScaleText( pRow, t = esLiteral ) {
    if ( !pRow?.scaleCents ) return '';
    const aVars = {
        rate: pRow.scaleCents % 100 === 0 ? esMoney( pRow.scaleCents, INTL_FALLBACK ) : pRow.scaleCents + '¢',
        per: esCount( pRow.scalePer ?? 1, INTL_FALLBACK ),
        free: esCount( pRow.scaleFree ?? 0, INTL_FALLBACK ),
    };
    if ( pRow.scaleBlock ) {
        return pRow.scaleFree ? t( 'common-finance-scale-block-past', '+{rate} per started {per} accounts past {free}', aVars )
            : t( 'common-finance-scale-block', '+{rate} per started {per} accounts', aVars );
    }
    return pRow.scaleFree ? t( 'common-finance-scale-rate-past', '+{rate} per {per} accounts past {free}', aVars )
        : t( 'common-finance-scale-rate', '+{rate} per {per} accounts', aVars );
}

/** "2400" from 2400 in the reader's digits: a count reads without a thousands comma (Tim, 2026-10-07). */
export function esCount( pCount, pTag ) {
    return esFormatNumber( pCount ?? 0, COUNT, pTag );
}

const COUNT = { useGrouping: false };

/**
 * A goal as one bar in month divisions, twelve for a year and three for a quarter,
 * each filling green as the goal's fill covers that month of it.
 */
export function EsFinanceBar( { label, have, goal, months = 12, 'data-testid': dataTestId } ) {
    const aMonths = goal > 0 ? months * ( have ?? 0 ) / goal : months;

    return (
        <div className="flex gap-1" role="progressbar" aria-label={ label }
             aria-valuemin={ 0 } aria-valuemax={ goal } aria-valuenow={ have }
             data-testid={ `${ dataTestId }-bar` }>
            { Array.from( { length: months }, ( _, pIndex ) => (
                <span key={ pIndex } className="h-3 flex-1 overflow-hidden rounded-sm bg-code"
                      data-testid={ `${ dataTestId }-month` }>
                    <span className="block h-full bg-goal-met"
                          style={ { width: `${ 100 * Math.min( 1, Math.max( 0, aMonths - pIndex ) ) }%` } } />
                </span>
            ) ) }
        </div>
    );
}

/** One goal: what it is and its fill against it -- the subscribers it holds, hosting
    filling before Tim, quarter by quarter (esback's summary) -- with an aside at the row's right end
    where one is given, the bar, then a rate line under it where one is given. The year's
    meters put the horizon in the aside and the rate below; the quarter's put "N more" in the
    aside and nothing below (Tim, 2026-10-11). The home page and the Subscribe page draw the
    same meter. */
export function EsFinanceMeter( { name, have, goal, rate, aside, months, 'data-testid': dataTestId } ) {
    const t = useEsStrings();
    const aCount = t( 'common-finance-meter-count', '{have} of {goal} subscribers', { have: esCount( have ), goal: esCount( goal ) } );
    return (
        <div className="mb-6" data-testid={ dataTestId }>
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4">
                <p className="font-semibold text-body" data-testid={ `${ dataTestId }-label` }>
                    { name }: <span className="font-normal tabular-nums" data-testid={ `${ dataTestId }-count` }>
                        { aCount }
                    </span>
                </p>
                { aside && <span className="text-sm text-aside whitespace-nowrap tabular-nums" data-testid={ `${ dataTestId }-aside` }>
                    { aside }
                </span> }
            </div>
            <EsFinanceBar label={ `${ name }: ${ aCount }` }
                          have={ have } goal={ goal } months={ months } data-testid={ dataTestId } />
            { rate && <p className="mt-1 text-sm text-muted tabular-nums" data-testid={ `${ dataTestId }-rate` }>{ rate }</p> }
        </div>
    );
}
